import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/ui/tooltip';
import { AlertTriangle } from 'lucide-react';

interface ResourceUtilizationBarProps {
  utilizationPercent: number;
  bookedHours: number;
  availableHours: number;
  /** Optional list of member names on leave during this window. */
  membersOnLeave?: string[];
  /** Compact mode hides the numeric label and shrinks the bar. */
  compact?: boolean;
  className?: string;
}

function bandFor(utilizationPercent: number): {
  fill: string;
  label: 'under' | 'near' | 'over';
} {
  if (utilizationPercent > 100) return { fill: 'bg-red-500', label: 'over' };
  if (utilizationPercent >= 90) return { fill: 'bg-yellow-500', label: 'near' };
  return { fill: 'bg-green-500', label: 'under' };
}

export function ResourceUtilizationBar({
  utilizationPercent,
  bookedHours,
  availableHours,
  membersOnLeave = [],
  compact = false,
  className = '',
}: ResourceUtilizationBarProps) {
  const band = bandFor(utilizationPercent);
  const clamped = Math.min(Math.max(utilizationPercent, 0), 100);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className={`flex items-center gap-1.5 ${className}`}>
          <div
            className={`relative ${compact ? 'h-1' : 'h-1.5'} flex-1 min-w-[60px] rounded-full bg-muted overflow-hidden`}
          >
            <div
              className={`h-full ${band.fill} transition-all`}
              style={{ width: `${clamped}%` }}
            />
          </div>
          {!compact && (
            <span
              className={`text-[11px] font-medium tabular-nums ${
                band.label === 'over'
                  ? 'text-red-600 dark:text-red-400'
                  : band.label === 'near'
                    ? 'text-yellow-700 dark:text-yellow-400'
                    : 'text-muted-foreground'
              }`}
            >
              {Math.round(utilizationPercent)}%
            </span>
          )}
          {band.label === 'over' && !compact && (
            <AlertTriangle
              className="w-3.5 h-3.5 text-red-600 dark:text-red-400"
              aria-label="Over capacity"
            />
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent>
        <div className="space-y-0.5">
          <div className="tabular-nums">
            {bookedHours.toFixed(1)}h / {availableHours}h
            <span className="ml-1 text-muted-foreground">({utilizationPercent.toFixed(0)}%)</span>
          </div>
          {utilizationPercent > 100 && (
            <div className="text-red-600 dark:text-red-400 font-medium">Over capacity</div>
          )}
          {membersOnLeave.length > 0 && (
            <div className="text-muted-foreground">On leave: {membersOnLeave.join(', ')}</div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}
